import type { Category } from '@/types/category';
import type { Course } from '@/types/course';
import type { ExpertData } from '@/types/expert';
import type { FAQ, FAQCategory } from '@/types/faq';
import type { HomepageResponse } from '@/types/homepage';
import type { Language } from '@/types/language';
import type { Partner } from '@/types/partner';
import type { VideoCourseResponse, VideoResponse } from '@/types/video';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { languageTranslations } from '@/utils/translations';
import type { SearchHit } from '@/types/search';
import { SearchableStrapiEntities } from '@/types/search';
import type { StrapiResponse, StrapiBaseQueryParams } from '@/types/strapi';

const API_URL = process.env.NEXT_PUBLIC_STRAPI_URL;
const API_TOKEN = process.env.NEXT_PUBLIC_STRAPI_API_TOKEN;
export const DEFAULT_STRAPI_SORT = ['updatedAt:desc'];

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    Authorization: `Bearer ${API_TOKEN}`,
  },
});

export const getStrapiData = async <T>(
  endpoint: string,
  params?: StrapiBaseQueryParams | object,
): Promise<StrapiResponse<T>> => {
  try {
    const response: AxiosResponse<StrapiResponse<T>> = await axiosInstance.get(`/api/${endpoint}`, {
      params,
    });
    return response.data;
  } catch (_error) {
    console.error('Error fetching data from Strapi');
    throw new Error('Failed to fetch data from Strapi');
  }
};
export const getCourses = async (
  limit: number,
  sort: string[],
  start: number,
  locale: string,
): Promise<StrapiResponse<Course[]>> => {
  const today = new Date().toISOString();

  return getStrapiData<Course[]>('courses', {
    populate: 'cover, expert.picture, languages, categories',
    'pagination[limit]': limit,
    'pagination[start]': start,
    sort: sort,
    locale,
    filters: {
      date: {
        $gte: today,
      },
    },
  });
};

export const getPartner = async (id: string, locale: string): Promise<StrapiResponse<Partner>> => {
  return getStrapiData<Partner>(`partners/${id}`, {
    locale,
    populate: 'cover,logo,features,languages',
  });
};

export const getPartners = async (
  limit: number,
  start: number,
  sort: string[] = DEFAULT_STRAPI_SORT,
  locale: string,
): Promise<StrapiResponse<Partner[]>> => {
  return getStrapiData<Partner[]>('partners', {
    populate: 'cover,logo,languages,categories,features',
    'pagination[limit]': limit,
    'pagination[start]': start,
    sort: sort,
    locale,
  });
};

export const getCourse = async (id: string): Promise<StrapiResponse<Course>> => {
  return getStrapiData<Course>(`courses/${id}`, {
    populate: 'expert.picture, cover',
  });
};

export const getFaqs = async (locale: string): Promise<StrapiResponse<FAQ[]>> => {
  return getStrapiData<FAQ[]>('faqs', {
    populate: 'faq_category',
    locale,
  });
};

export const getFaqCategories = async (locale: string): Promise<StrapiResponse<FAQCategory[]>> => {
  return getStrapiData<FAQCategory[]>('faq-categories', {
    locale,
  });
};

export const getExpert = async (
  id: string,
  locale: string,
): Promise<StrapiResponse<ExpertData>> => {
  return getStrapiData<ExpertData>(`experts/${id}`, {
    locale,
    populate: 'picture,languages,categories',
  });
};

export const getExpertByExternalId = async (
  externalId: string,
): Promise<StrapiResponse<ExpertData[]>> => {
  return getStrapiData<ExpertData[]>('experts', {
    filters: {
      external_id: externalId,
    },
    populate: 'picture',
  });
};

export const getExperts = async (
  limit: number,
  skip: number,
  filters: { [key: string]: string | string[] },
  locale: string,
  sort: string[] = DEFAULT_STRAPI_SORT,
  //excludeIds: number[] = [],
): Promise<StrapiResponse<ExpertData[]>> => {
  const queryParams: { [key: string]: string | string[] } = {
    populate: 'languages,picture,categories',
    'pagination[limit]': limit.toString(),
    'pagination[start]': skip.toString(),
    'filters[active][$eq]': 'true',
    locale,
    sort,
  };

  /*if (excludeIds.length > 0) {
    queryParams[`filters[id][$notIn]`] = excludeIds.map(String);
  }*/

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'languages.name') {
      queryParams[`filters[languages][name][$in]`] = Array.isArray(value) ? value : [value];
    } else if (key === 'categories.slug' && Array.isArray(value)) {
      // Construct the $and condition for multiple categories
      value.forEach((slug, index) => {
        queryParams[`filters[$and][${index}][categories][slug][$eq]`] = slug;
      });
    } else if (Array.isArray(value)) {
      queryParams[`filters[${key}][$in]`] = value;
    } else {
      queryParams[`filters[${key}][$eq]`] = value;
    }
  });

  const data = await getStrapiData<ExpertData[]>('experts', queryParams);

  // Dynamically translate the `languages` field
  data.data.forEach((expert) => {
    expert.attributes.languages.data = expert.attributes.languages.data.map((language) => ({
      ...language,
      attributes: {
        ...language.attributes,
        name: languageTranslations[language.attributes.name]?.[locale] || language.attributes.name,
      },
    }));
  });
  return data;
};

export const getVideoCourse = async (id: string): Promise<StrapiResponse<VideoCourseResponse>> => {
  return getStrapiData<VideoCourseResponse>(`video-courses/${id}`, {
    populate: 'videos, expert.picture, thumbnail',
  });
};

export const getVideoCourses = async (
  limit: number,
): Promise<StrapiResponse<VideoCourseResponse[]>> => {
  return getStrapiData<VideoCourseResponse[]>('video-courses', {
    populate: 'videos, expert.picture, thumbnail',
    pagination: {
      limit,
    },
  });
};

export const getVideo = async (id: string): Promise<StrapiResponse<VideoResponse>> => {
  return getStrapiData<VideoResponse>(`videos/${id}`, {
    populate: 'expert.picture',
  });
};

export const getLanguages = async (locale: string): Promise<StrapiResponse<Language[]>> => {
  try {
    const languages = await getStrapiData<Language[]>(`Languages`, {
      populate: '*',
      'pagination[limit]': 100,
    });

    // const normalizedLocale = locale.slice(0, 2);

    languages.data = languages.data.map((language) => ({
      ...language,
      attributes: {
        ...language.attributes,
        localizedName:
          languageTranslations[language.attributes.name]?.[locale] || language.attributes.name,
      },
    }));

    return languages;
  } catch (error) {
    console.error('Error fetching languages:', error);
    throw error;
  }
};

export const getCategories = async (locale: string | null): Promise<StrapiResponse<Category[]>> => {
  return getStrapiData<Category[]>('categories', {
    locale,
    sort: 'position:asc',
  });
};

export const getHomepage = async (): Promise<StrapiResponse<HomepageResponse>> => {
  return getStrapiData<HomepageResponse>(`homepages`, {
    populate: '*',
  });
};

export const searchStrapi = async (query: string, locale: string): Promise<SearchHit[]> => {
  const expertsPromise = getStrapiData<ExpertData[]>('experts', {
    locale,
    fields: ['name', 'description'],
    filters: {
      $and: [
        {
          $or: [
            {
              name: {
                $containsi: query,
              },
            },
            {
              description: {
                $containsi: query,
              },
            },
          ],
        },
        {
          active: true,
        },
      ],
    },
  }).then((result) => {
    return result.data.map((element) => {
      return {
        id: element.id,
        title: element.attributes.name,
        description: element.attributes.description,
        type: SearchableStrapiEntities.experts,
      };
    });
  });

  const partnersPromise = getStrapiData<Partner[]>('partners', {
    locale,
    fields: ['name', 'short_description'],
    filters: {
      $or: [
        {
          name: {
            $containsi: query,
          },
        },
        {
          short_description: {
            $containsi: query,
          },
        },
      ],
    },
  }).then((result) => {
    return result.data.map((element) => {
      return {
        id: element.id,
        title: element.attributes.name,
        description: element.attributes.short_description,
        type: SearchableStrapiEntities.partners,
      };
    });
  });

  const [experts, partners] = await Promise.allSettled([expertsPromise, partnersPromise]);
  return [
    ...(experts.status == 'fulfilled' ? experts.value : []),
    ...(partners.status == 'fulfilled' ? partners.value : []),
  ];
};

export const fetchFromStrapi = async <T>(
  endpoint: string,
  params?: Record<string, unknown>,
): Promise<StrapiResponse<T> | null> => {
  try {
    const response: AxiosResponse<StrapiResponse<T>> = await axiosInstance.get(`/api/${endpoint}`, {
      params,
    });
    return response.data;
  } catch (_error) {
    console.error('Error fetching data from Strapi');
    return null;
  }
};
